// Чистата логика на справката „Прехвърляния": филтрите в колоните (От склад ·
// Към склад · Потребител · Статус), търсенето, сортирането по заглавията и
// сумите. Сегментите Всички / Активни / Анулирани са пряк път към филтъра
// „Статус" — едно и също състояние. Броячите са фасетни: по периода след
// търсенето и ОСТАНАЛИТЕ филтри (`components/filters/columnFilterModel.ts`).
import type { TransferDocument } from "../../../../api/inventoryTransfers";
import {
  directionOf,
  fromWarehouseOf,
  isTransferAnnulled,
  toWarehouseOf,
  transferUserOf,
} from "../../transfer/components/transferMeta";
import type { FilterSpecs } from "../../components/filters/columnFilterModel";

export type TransferStatusKey = "all" | "active" | "annulled";

export type TransferFilters = {
  from: string[];
  to: string[];
  users: string[];
  statuses: string[]; // active | annulled
};

export const EMPTY_TRANSFER_FILTERS: TransferFilters = { from: [], to: [], users: [], statuses: [] };

export type TransferFilterKey = keyof TransferFilters;

// Сортиране по всяка колона, както в стария списък
export type TransferSortKey = "date" | "direction" | "user" | "note" | "products" | "net" | "gross";
export type TransferSort = { key: TransferSortKey; dir: "asc" | "desc" };

// Текстовите колони тръгват от А, числовите и датата — от най-голямото
export const TEXT_SORT_KEYS: TransferSortKey[] = ["direction", "user", "note"];

export const transferStatusOf = (transfer: TransferDocument): Exclude<TransferStatusKey, "all"> =>
  isTransferAnnulled(transfer) ? "annulled" : "active";

// Сегментът е изгледът на филтъра „Статус": точно един → той, иначе „Всички"
export const statusSegmentOf = (statuses: string[]): TransferStatusKey =>
  statuses.length === 1 && (statuses[0] === "active" || statuses[0] === "annulled") ? statuses[0] : "all";

export type TransferFilterLabels = {
  titles: Record<TransferFilterKey, string>;
  statusLabel: (value: string) => string;
};

export const transferFilterSpecs = ({
  titles,
  statusLabel,
}: TransferFilterLabels): FilterSpecs<TransferDocument, TransferFilters> => ({
  from: { kind: "list", title: titles.from, valuesOf: (transfer) => [fromWarehouseOf(transfer)] },
  to: { kind: "list", title: titles.to, valuesOf: (transfer) => [toWarehouseOf(transfer)] },
  users: { kind: "list", title: titles.users, valuesOf: (transfer) => [transferUserOf(transfer)] },
  statuses: {
    kind: "list",
    title: titles.statuses,
    valuesOf: (transfer) => [transferStatusOf(transfer)],
    labelOf: statusLabel,
    order: ["active", "annulled"],
  },
});

const matchesQuery = (transfer: TransferDocument, words: string[]) => {
  const haystack = [
    `№${transfer.id}`,
    directionOf(transfer),
    transferUserOf(transfer),
    transfer.note ?? "",
    ...transfer.lines.map((line) => line.productName),
  ]
    .join(" ")
    .toLowerCase();
  return words.every((word) => haystack.includes(word));
};

// Търсачката над таблицата — базата, върху която работят филтрите в колоните
export const searchTransfers = (transfers: TransferDocument[], query: string) => {
  const words = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
  return words.length ? transfers.filter((transfer) => matchesQuery(transfer, words)) : transfers;
};

const compareBy = (key: TransferSortKey, a: TransferDocument, b: TransferDocument) => {
  switch (key) {
    case "date":
      return a.date.localeCompare(b.date);
    case "direction":
      return directionOf(a).localeCompare(directionOf(b), "bg");
    case "user":
      return transferUserOf(a).localeCompare(transferUserOf(b), "bg");
    case "note":
      return (a.note ?? "").localeCompare(b.note ?? "", "bg");
    case "products":
      return (a.productsCount || 0) - (b.productsCount || 0);
    case "net":
      return (a.totalValueNoVat || 0) - (b.totalValueNoVat || 0);
    case "gross":
      return (a.totalValueWithVat || 0) - (b.totalValueWithVat || 0);
  }
};

export const sortTransfers = (transfers: TransferDocument[], sort: TransferSort) => {
  const sign = sort.dir === "asc" ? 1 : -1;
  return [...transfers].sort((a, b) => compareBy(sort.key, a, b) * sign || b.id - a.id);
};

// Сумите и KPI — САМО активните (анулираните стоят зачертани в списъка)
export const transferTotals = (transfers: TransferDocument[]) => {
  const active = transfers.filter((transfer) => !isTransferAnnulled(transfer));
  return {
    count: active.length,
    products: active.reduce((sum, transfer) => sum + (transfer.productsCount || 0), 0),
    net: active.reduce((sum, transfer) => sum + (transfer.totalValueNoVat || 0), 0),
    gross: active.reduce((sum, transfer) => sum + (transfer.totalValueWithVat || 0), 0),
  };
};
